import { create } from "zustand";
import axios from "axios";

const useUserStore = create((set, get) => ({
  userDetails: null,
  userLoading: false,
  userError: null,
  fetchUserDetails: async () => {
    /* Navbar's effect re-runs whenever userDetails or userLoading changes,
       and its condition is `!userDetails && !userLoading`. A failed fetch
       left both falsy, so the effect fired again immediately — an unbounded
       request loop (observed: 590 failed calls to /auth/v1/user in one
       session). Once a fetch has failed, stop until something clears it. */
    const { userLoading, userError } = get();
    if (userLoading || userError) return;

    set({ userLoading: true, userError: null });
    try {
      const token = localStorage.getItem("userToken");
      if (!token) {
        set({ userLoading: false });
        return;
      }
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/auth/v1/user`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.data.status) {
        set({ userDetails: res.data.data, userLoading: false });
      } else {
        set({ userError: "Failed to fetch user details.", userLoading: false });
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error);

      /* A token the server will not accept must be thrown away, or the app
         stays stuck: isLoggedIn is just `!!localStorage.userToken`, so a
         dead token makes the navbar show "Log out" and hide "Log in" — no
         way back to a working session.

         The 500 case is deliberate: this API's auth middleware answers an
         invalid token with 500 "Invalid Token" rather than 401, so checking
         only for 401/403 would miss the common case. A 500 whose message is
         anything else is treated as a server problem and the token is kept,
         so a backend outage does not sign everybody out. */
      const status = error.response?.status;
      const message = error.response?.data?.message;
      const tokenRejected =
        status === 401 || status === 403 || message === "Invalid Token";

      if (tokenRejected) {
        localStorage.removeItem("userToken");
        set({
          userDetails: null,
          userLoading: false,
          userError: null,
        });
        return;
      }

      set({ userError: "Failed to fetch user details.", userLoading: false });
    }
  },
  /* Also resets userError, which is what re-arms fetchUserDetails after a
     failure — otherwise the guard above would block every later attempt for
     the lifetime of the page. */
  clearUserDetails: () =>
    set({ userDetails: null, userLoading: false, userError: null }),
}));

export default useUserStore;
